import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CapabilityStateControl } from "@/components/capabilities/CapabilityStateControl";
import { capabilityKeys } from "@/hooks/useCapabilities";
import {
  createCapabilityOverride,
  deleteCapabilityOverride,
  type CapabilityOverride,
} from "@/services/capabilitiesApi";
import type { TripwirePin } from "@/utils/tripwireBadge";
import {
  OVERRIDE_REMOVED_THEN_FAILED,
  OverrideRemovedThenFailedError,
  syncCapabilityPin,
} from "@/utils/syncCapabilityPin";
import { toast } from "sonner";

// Both are OPS capabilities with no source and no user — scope_id is always
// "" (global). There is deliberately no parent "catalog.cron" capability;
// see the plan's naming note.
const GATE = "catalog.cron.gate";
const SWEEP = "catalog.cron.sweep";

function pinFromRow(row: CapabilityOverride | undefined): TripwirePin {
  if (!row) return "inherit";
  return row.state === "allow" ? "allow" : "deny";
}

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" with no timezone.
function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDatetimeLocalValue(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

function needsWrite(
  row: CapabilityOverride | undefined,
  pin: TripwirePin,
  expiresAtInput: string,
): boolean {
  if (pin === "inherit") return false;
  const changedPin = pin !== pinFromRow(row);
  const changedExpiry =
    expiresAtInput !== toDatetimeLocalValue(row?.expires_at);
  return changedPin || changedExpiry;
}

export function CatalogCronDialog({
  open,
  onOpenChange,
  gateRow,
  sweepRow,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gateRow: CapabilityOverride | undefined;
  sweepRow: CapabilityOverride | undefined;
}) {
  const queryClient = useQueryClient();
  const [gatePin, setGatePin] = useState<TripwirePin>("inherit");
  const [sweepPin, setSweepPin] = useState<TripwirePin>("inherit");
  const [gateExpiresAt, setGateExpiresAt] = useState("");
  const [sweepExpiresAt, setSweepExpiresAt] = useState("");
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const wasOpen = useRef(false);

  useEffect(() => {
    const opened = open && !wasOpen.current;
    wasOpen.current = open;
    if (!opened) return;
    setGatePin(pinFromRow(gateRow));
    setSweepPin(pinFromRow(sweepRow));
    setGateExpiresAt(toDatetimeLocalValue(gateRow?.expires_at));
    setSweepExpiresAt(toDatetimeLocalValue(sweepRow?.expires_at));
    setReason("");
  }, [open, gateRow, sweepRow]);

  const writeNeedsReason = useMemo(
    () =>
      needsWrite(gateRow, gatePin, gateExpiresAt) ||
      needsWrite(sweepRow, sweepPin, sweepExpiresAt),
    [gateRow, gatePin, gateExpiresAt, sweepRow, sweepPin, sweepExpiresAt],
  );

  const handleSubmit = async () => {
    if (writeNeedsReason && !reason.trim()) return;
    setPending(true);
    try {
      const gateChanged = await syncCapabilityPin({
        capability: GATE,
        scopeType: "global",
        scopeId: "",
        current: gateRow,
        desired: gatePin,
        reason: reason.trim(),
        expiresAt: fromDatetimeLocalValue(gateExpiresAt),
        create: createCapabilityOverride,
        remove: deleteCapabilityOverride,
        onUnchanged: () =>
          toast.message(
            "That override was saved, but it didn't change the current decision. Did you mean Inherit?",
          ),
      });
      const sweepChanged = await syncCapabilityPin({
        capability: SWEEP,
        scopeType: "global",
        scopeId: "",
        current: sweepRow,
        desired: sweepPin,
        reason: reason.trim(),
        expiresAt: fromDatetimeLocalValue(sweepExpiresAt),
        create: createCapabilityOverride,
        remove: deleteCapabilityOverride,
        onUnchanged: () =>
          toast.message(
            "That override was saved, but it didn't change the current decision. Did you mean Inherit?",
          ),
      });
      await queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
      if (!gateChanged && !sweepChanged) {
        toast.message("No schedule changes.");
      } else {
        toast.success("Updated catalog cron schedule");
      }
      onOpenChange(false);
    } catch (error) {
      await queryClient.invalidateQueries({ queryKey: capabilityKeys.all });
      if (error instanceof OverrideRemovedThenFailedError) {
        toast.error(OVERRIDE_REMOVED_THEN_FAILED);
      } else {
        toast.error(
          error instanceof Error ? error.message : "Failed to update schedule",
        );
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manage scheduled generation</DialogTitle>
          <DialogDescription>
            Global overrides on the two crontab entries on the API box. Inherit
            means no override row — the schedule runs. "Run now" and "Run on
            Spot" are unaffected by either switch.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Delta gate — every 5 minutes</Label>
            <p className="text-xs text-muted-foreground">
              Renders up to 200 missing combos when the corpus moves.
            </p>
            <CapabilityStateControl
              value={gatePin}
              onChange={setGatePin}
              denyLabel="Deny"
              disabled={pending}
            />
            {gatePin !== "inherit" && (
              <div className="space-y-1">
                <Label htmlFor="gate-expires-at">Expires (optional)</Label>
                <Input
                  id="gate-expires-at"
                  type="datetime-local"
                  value={gateExpiresAt}
                  onChange={(event) => setGateExpiresAt(event.target.value)}
                  disabled={pending}
                  className="w-56"
                />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Nightly sweep — 02:00 UTC</Label>
            <p className="text-xs text-muted-foreground">
              Reconciles every missing combo with no limit, then tombstones
              orphaned rows.
            </p>
            <CapabilityStateControl
              value={sweepPin}
              onChange={setSweepPin}
              denyLabel="Deny"
              disabled={pending}
            />
            {sweepPin !== "inherit" && (
              <div className="space-y-1">
                <Label htmlFor="sweep-expires-at">Expires (optional)</Label>
                <Input
                  id="sweep-expires-at"
                  type="datetime-local"
                  value={sweepExpiresAt}
                  onChange={(event) => setSweepExpiresAt(event.target.value)}
                  disabled={pending}
                  className="w-56"
                />
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="catalog-cron-reason">Reason</Label>
            <Textarea
              id="catalog-cron-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Required when writing an allow or deny row"
              disabled={pending}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={pending || (writeNeedsReason && !reason.trim())}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
